import { useState } from "react";
import { API } from "@/api/client";

/** Presentation-layer safety net: strip any fenced code block or <think>/<reasoning> tag the
 * model might still emit despite the hardened system prompt (config/ai_routing.json) — the
 * assistant should only ever surface a clean prose answer with citations, never raw tool-call
 * code or chain-of-thought. Applied at render time, not to the gateway/stream itself. */
function stripReasoningArtifacts(text) {
  if (!text) return text;
  return text
    .replace(/```[\s\S]*?```/g, "")
    .replace(/<(think|reasoning|scratchpad)>[\s\S]*?<\/\1>/gi, "")
    .trim();
}

/** Shared AI Assistant chat engine — used by both the ⌘J side panel and the full-page
 * /ai_assistant route so behaviour (streaming, citations, code-fence stripping, default fast
 * model for untargeted asks) never drifts between the two entry points. */
export function useAiChat() {
  const [messages, setMessages] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [status, setStatus] = useState(null);
  const [provider, setProvider] = useState(null);
  const [contextRecords, setContextRecords] = useState([]);

  const send = async (context = {}) => {
    if (!prompt.trim() || streaming) return;
    setMessages((m) => [...m, { role: "user", text: prompt }, { role: "ai", text: "" }]);
    const currentPrompt = prompt;
    setPrompt("");
    setStreaming(true);
    setStatus("Searching your workspace…");
    setContextRecords([]);

    // Demo-mode sessions use a localStorage Bearer token; Google-mode sessions use an httpOnly
    // cookie instead — only attach the header when we actually have a token (see AiSidePanel's
    // original note on why a literal "Bearer null" header is worse than no header at all).
    const token = localStorage.getItem("njm_token");
    // No explicit skill (e.g. a bare open, not "summarise this client") -> route to the faster
    // "quick_answer" model (config-only routing rule, ai_routing.json) instead of paying the
    // latency of the heavier default model for a simple ask.
    const skill = context.skill || "quick_answer";
    try {
      const res = await fetch(`${API}/v1/ai/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ prompt: currentPrompt, scope: context, skill }),
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try { detail = (await res.json())?.detail || detail; } catch {}
        throw new Error(detail);
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      let gotFirstToken = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data:\s*/, "");
          if (line === "[DONE]" || !line) continue;
          try {
            const ev = JSON.parse(line);
            if (ev.type === "context") setContextRecords(ev.records || []);
            else if (ev.type === "delta") {
              if (!gotFirstToken) { setStatus(null); gotFirstToken = true; }
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], text: (copy[copy.length - 1].text || "") + ev.content };
                return copy;
              });
            } else if (ev.type === "done") setProvider(`${ev.provider} · ${ev.model}`);
            else if (ev.type === "error") {
              setMessages((m) => { const c = [...m]; c[c.length - 1].text = `\u26a0\ufe0f ${ev.content}`; return c; });
            }
          } catch {}
        }
      }
    } catch (e) {
      setMessages((m) => { const c = [...m]; c[c.length - 1].text = `\u26a0\ufe0f ${e.message}`; return c; });
    } finally {
      setStreaming(false);
      setStatus(null);
    }
  };

  const displayMessages = messages.map((m) => (m.role === "ai" ? { ...m, text: stripReasoningArtifacts(m.text) } : m));
  return { messages: displayMessages, prompt, setPrompt, send, streaming, status, provider, contextRecords };
}
